import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type Theme = "light" | "dark" | "system";
export type DealScope = "mine" | "all";

interface UIState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;

  theme: Theme;
  setTheme: (t: Theme) => void;

  // Deals screen: "Mine" (deals I own) or "All" (everything I can see).
  dealScope: DealScope;
  setDealScope: (s: DealScope) => void;

  // Owner filter (Activity feed). Holds a user id; null = all owners.
  ownerUserId: string | null;
  setOwnerUserId: (id: string | null) => void;

  // The deal open in the side panel, if any. Ephemeral — not persisted.
  openDealId: string | null;
  openDeal: (id: string) => void;
  closeDeal: () => void;

  // Phone-width navigation drawer. Ephemeral — not persisted.
  mobileNavOpen: boolean;
  setMobileNavOpen: (v: boolean) => void;

  // Command palette (⌘K). Ephemeral — not persisted.
  cmdkOpen: boolean;
  openCmdK: () => void;
  closeCmdK: () => void;
  toggleCmdK: () => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),

      theme: "light",
      setTheme: (theme) => set({ theme }),

      dealScope: "mine",
      setDealScope: (dealScope) => set({ dealScope }),

      ownerUserId: null,
      setOwnerUserId: (id) => set({ ownerUserId: id }),

      openDealId: null,
      openDeal: (id) => set({ openDealId: id, cmdkOpen: false }),
      closeDeal: () => set({ openDealId: null }),

      mobileNavOpen: false,
      setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),

      cmdkOpen: false,
      openCmdK: () => set({ cmdkOpen: true, mobileNavOpen: false }),
      closeCmdK: () => set({ cmdkOpen: false }),
      toggleCmdK: () => set((s) => ({ cmdkOpen: !s.cmdkOpen })),
    }),
    {
      name: "techmellon-crm-ui",
      storage: createJSONStorage(() => localStorage),
      // Only persist the long-lived UI prefs, not ephemeral overlay state.
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        ownerUserId: s.ownerUserId,
        theme: s.theme,
        dealScope: s.dealScope,
      }),
    },
  ),
);
